import {
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  List,
  Text,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import type { Campaign } from "../../api/campaigns";

const PLACEMENT_WHERE: Record<string, string> = {
  product: "a product page",
  cart: "the cart page",
  cart_drawer: "the cart drawer",
  homepage: "your homepage",
};

const PLACEMENT_LABEL: Record<string, string> = {
  product: "Product page",
  cart: "Cart page",
  cart_drawer: "Cart drawer",
  homepage: "Homepage",
};

function placementWhere(placement: string): string {
  return PLACEMENT_WHERE[placement] ?? "the matching page on your store";
}

function placementLabel(placement: string): string {
  return PLACEMENT_LABEL[placement] ?? placement;
}

function delaySecondsFromMs(triggerDelayMs: string | number | null | undefined): number {
  if (triggerDelayMs == null) return 3;
  const n = typeof triggerDelayMs === "number" ? triggerDelayMs : parseInt(String(triggerDelayMs).trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 3;
  return Math.max(1, Math.round(n / 1000));
}

/** One merchant-friendly sentence: where + how + when. */
export function describeStorefrontAppearance(input: {
  placement: string;
  displayMode: string;
  triggerType: string;
  triggerDelayMs: string;
}): string {
  const where = placementWhere(input.placement);
  const label = placementLabel(input.placement);
  const mode = (input.displayMode ?? "modal").toLowerCase();
  const trigger = (input.triggerType ?? "immediate").toLowerCase();
  const sec = delaySecondsFromMs(input.triggerDelayMs);

  if (mode === "inline") {
    return `On ${label.toLowerCase()}, shoppers see the game inline on the page as soon as that page loads (open ${where}).`;
  }
  if (trigger === "delay") {
    return `On ${label.toLowerCase()}, the game opens in a popup after about ${sec} second${sec === 1 ? "" : "s"} — open ${where} and wait to see it.`;
  }
  return `On ${label.toLowerCase()}, the game opens in a popup shortly after the page loads — visit ${where} to see it.`;
}

export type StorefrontSetupGuidanceVariant = "campaign" | "dashboard";

export interface StorefrontSetupGuidanceProps {
  variant: StorefrontSetupGuidanceVariant;
  /** Campaign form only — drives contextual copy */
  placement?: string;
  displayMode?: string;
  triggerType?: string;
  triggerDelayMs?: string;
  campaignStatus?: Campaign["status"];
  isEditMode?: boolean;
  campaignId?: string;
  hasVariants?: boolean;
}

/**
 * In-app storefront setup: where the game shows, what to do next, how to test.
 * Primary onboarding surface for merchants (not a substitute for docs).
 */
export function StorefrontSetupGuidance({
  variant,
  placement = "product",
  displayMode = "modal",
  triggerType = "immediate",
  triggerDelayMs = "3000",
  campaignStatus = "DRAFT",
  isEditMode = false,
  campaignId,
  hasVariants = false,
}: StorefrontSetupGuidanceProps) {
  const navigate = useNavigate();

  if (variant === "dashboard") {
    return (
      <Card>
        <Box padding="400">
          <BlockStack gap="400">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                Getting your game on the storefront
              </Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Games appear only when a campaign is Active, has a game variant, and matches the
                placement you chose (product page, cart, homepage, or cart drawer). The app
                injects the experience on your live store — open the matching page to test.
              </Text>
            </BlockStack>
            <List type="bullet">
              <List.Item>
                Create or open a campaign and set <strong>Display settings</strong> (where it
                shows, popup vs inline, immediate vs delayed).
              </List.Item>
              <List.Item>
                Add a game variant, connect rewards, then set the campaign to <strong>Active</strong>{" "}
                and save.
              </List.Item>
              <List.Item>
                On your live storefront, open the page that matches the placement (for example
                cart for “Cart page”) and confirm the wheel or scratch appears.
              </List.Item>
            </List>
            <InlineStack gap="200" wrap>
              <Button variant="primary" onClick={() => navigate("/campaigns")}>
                Manage campaigns
              </Button>
              <Button onClick={() => navigate("/games")}>Browse games</Button>
            </InlineStack>
          </BlockStack>
        </Box>
      </Card>
    );
  }

  const appearance = describeStorefrontAppearance({
    placement,
    displayMode,
    triggerType,
    triggerDelayMs,
  });

  const nextSteps: string[] = [];
  if (!isEditMode) {
    nextSteps.push("Save this campaign, then add at least one game variant under Variants.");
  } else {
    if (!hasVariants) {
      nextSteps.push("Add at least one game variant and reward policy in Variants & offer setup.");
    }
    if (campaignStatus !== "ACTIVE") {
      nextSteps.push(
        "Set status to Active and save so the storefront can serve this campaign (when schedule and targeting allow)."
      );
    }
  }
  nextSteps.push(
    `On your live store, open ${placementWhere(placement)} — that is where shoppers will see this experience.`
  );
  if ((displayMode ?? "modal").toLowerCase() === "modal" && (triggerType ?? "immediate").toLowerCase() === "delay") {
    nextSteps.push(
      `If you use a delay, wait about ${delaySecondsFromMs(triggerDelayMs)} second(s) on that page for the popup.`
    );
  }
  nextSteps.push("Use a normal browser session (or preview your theme) to confirm the game appears as expected.");

  return (
    <Card>
      <div style={{ padding: "1rem" }}>
        <BlockStack gap="400">
          <BlockStack gap="100">
            <Text variant="headingMd" as="h2">
              How this appears on your store
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Your theme must load the BeyondGames storefront script (app embed). Once it does, the
              following applies to this campaign.
            </Text>
          </BlockStack>

          <BlockStack gap="200">
            <Text as="p" variant="bodyMd">
              {appearance}
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              Placement: <strong>{placementLabel(placement)}</strong> · Display:{" "}
              {(displayMode ?? "modal").toLowerCase() === "inline" ? "Inline" : "Modal"} · Trigger:{" "}
              {(triggerType ?? "immediate").toLowerCase() === "delay"
                ? `Delayed (~${delaySecondsFromMs(triggerDelayMs)}s)`
                : "Immediate"}
            </Text>
          </BlockStack>

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              What to do next
            </Text>
            <List type="number">
              {nextSteps.map((line, i) => (
                <List.Item key={i}>{line}</List.Item>
              ))}
            </List>
          </BlockStack>

          <BlockStack gap="200">
            <Text variant="headingSm" as="h3">
              How to test safely
            </Text>
            <List type="bullet">
              <List.Item>Use your published storefront (or theme preview), not only the admin.</List.Item>
              <List.Item>Navigate to the page that matches your placement.</List.Item>
              <List.Item>
                {(displayMode ?? "modal").toLowerCase() === "modal" &&
                (triggerType ?? "immediate").toLowerCase() === "delay"
                  ? `Wait for the configured delay, then look for the popup.`
                  : (displayMode ?? "modal").toLowerCase() === "inline"
                    ? `Look for the game embedded on the page.`
                    : `The experience should appear shortly after the page loads.`}
              </List.Item>
            </List>
          </BlockStack>

          <InlineStack gap="200" wrap>
            <Button variant="primary" onClick={() => navigate("/campaigns")}>
              Review all campaigns
            </Button>
            {isEditMode && campaignId && campaignStatus === "ACTIVE" ? (
              <Button onClick={() => navigate(`/campaigns/${campaignId}/analytics`)}>
                View campaign analytics
              </Button>
            ) : null}
            {isEditMode && campaignId && campaignStatus !== "ACTIVE" ? (
              <Button onClick={() => navigate("/games")}>Browse games</Button>
            ) : null}
          </InlineStack>
        </BlockStack>
      </div>
    </Card>
  );
}
